import { Bar, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine, Area, AreaChart, CartesianGrid } from "recharts";
import { cn } from "@/lib/utils";
import { nok } from "@/lib/format";
import { compactKr } from "./money";

const C = { primary: "#2670F2", ink: "#1F2446", success: "#10B981", danger: "#F43F5E", warning: "#F59E0B", muted: "#C9CFDA", soft: "#8FB3F7" };
const axis = { fontSize: 11, fill: "#8a93a3" };

const Tip = ({ active, payload, label, abs = true, extra }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="min-w-[180px] rounded-xl bg-card px-3 py-2 text-[12px] shadow-pop" role="tooltip">
      <div className="mb-1 font-medium">{label}</div>
      {payload
        .filter((p) => p.value !== 0 && p.value != null)
        .map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-3 text-muted-foreground tabular">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color || p.stroke }} aria-hidden /> {p.name}
            </span>
            <span className="text-foreground">{nok(abs ? Math.abs(p.value) : p.value)}</span>
          </div>
        ))}
      {extra && extra(payload[0]?.payload)}
    </div>
  );
};

const Legend = ({ items, className }) => (
  <div className={cn("flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-muted-foreground", className)}>
    {items.map((it) => (
      <span key={it.label} className="inline-flex items-center gap-1.5">
        {it.line ? <span className="h-[2px] w-3.5 rounded-full" style={{ background: it.color }} aria-hidden /> : <span className="h-2 w-2 rounded-[3px]" style={{ background: it.color }} aria-hidden />}
        {it.label}
      </span>
    ))}
  </div>
);

/** 13-week cashflow: inflow up, outflow down, balance line. Prognose. */
export const CashflowChart = ({ weeks = [], height = 260, className, dense }) => {
  const data = weeks.map((w) => ({ ...w, out_suppliers: -Math.abs(w.out_suppliers || 0), out_fixed: -Math.abs(w.out_fixed || 0) }));
  return (
    <div className={cn("w-full", className)} data-testid="cashflow-chart">
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap={dense ? 3 : 8} stackOffset="sign">
            <CartesianGrid vertical={false} stroke="rgba(16,24,40,0.06)" />
            <XAxis dataKey="week" tick={axis} axisLine={false} tickLine={false} />
            <YAxis yAxisId="bars" tick={axis} axisLine={false} tickLine={false} tickFormatter={(v) => compactKr(v)} width={dense ? 0 : 64} hide={dense} />
            <YAxis yAxisId="line" orientation="right" tick={axis} axisLine={false} tickLine={false} tickFormatter={(v) => compactKr(v)} width={dense ? 0 : 64} hide={dense} domain={[(min) => Math.min(0, min * 0.9), (max) => max * 1.1]} />
            <Tooltip content={<Tip />} cursor={{ fill: "rgba(16,24,40,0.04)" }} />
            <ReferenceLine yAxisId="bars" y={0} stroke="#c9cfda" />
            <Bar yAxisId="bars" dataKey="in_receivables" name="Innbetalinger fra fakturaer" stackId="a" fill={C.primary} radius={0} isAnimationActive={false} maxBarSize={34} />
            <Bar yAxisId="bars" dataKey="in_planned" name="Planlagt fakturering" stackId="a" fill={C.soft} isAnimationActive={false} maxBarSize={34} />
            <Bar yAxisId="bars" dataKey="out_suppliers" name="Leverandørforfall" stackId="a" fill={C.danger} fillOpacity={0.75} isAnimationActive={false} maxBarSize={34} />
            <Bar yAxisId="bars" dataKey="out_fixed" name="Faste kostnader" stackId="a" fill={C.muted} isAnimationActive={false} maxBarSize={34} />
            <Line yAxisId="line" type="monotone" dataKey="balance" name="Saldo (prognose)" stroke={C.ink} strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <Legend className="mt-2" items={[{ label: "Innbetalinger fra fakturaer", color: C.primary }, { label: "Planlagt fakturering", color: C.soft }, { label: "Leverandørforfall", color: C.danger }, { label: "Faste kostnader", color: C.muted }, { label: dense ? "Saldo" : "Saldo (høyre akse)", color: C.ink, line: true }]} />
    </div>
  );
};

/** Monthly revenue: invoiced (actual) · contracted (forecast) · weighted pipeline. */
export const RevenueChart = ({ months = [], height = 240, className, showPipeline = true }) => (
  <div className={cn("w-full", className)} data-testid="revenue-chart">
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap={4}>
          <CartesianGrid vertical={false} stroke="rgba(16,24,40,0.06)" />
          <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} interval={months.length > 14 ? 1 : 0} />
          <YAxis tick={axis} axisLine={false} tickLine={false} tickFormatter={(v) => compactKr(v)} width={64} />
          <Tooltip content={<Tip />} cursor={{ fill: "rgba(16,24,40,0.04)" }} />
          <Bar dataKey="invoiced" name="Fakturert (faktisk)" stackId="a" fill={C.ink} fillOpacity={0.85} isAnimationActive={false} maxBarSize={30} />
          <Bar dataKey="contracted" name="Kontrakt (prognose)" stackId="a" fill={C.primary} isAnimationActive={false} maxBarSize={30} />
          {showPipeline && <Bar dataKey="pipeline_weighted" name="Pipeline (vektet)" stackId="a" fill={C.muted} isAnimationActive={false} maxBarSize={30} radius={[3, 3, 0, 0]} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
    <Legend className="mt-2" items={[{ label: "Fakturert (faktisk)", color: C.ink }, { label: "Kontraktsfestet (prognose)", color: C.primary }, ...(showPipeline ? [{ label: "Pipeline (vektet)", color: C.muted }] : [])]} />
  </div>
);

/** Project: revenue vs cost per month + cumulative margin. */
export const ProjectMonthsChart = ({ months = [], height = 220, className }) => (
  <div className={cn("w-full", className)} data-testid="project-months-chart">
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap={6}>
          <CartesianGrid vertical={false} stroke="rgba(16,24,40,0.06)" />
          <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} />
          <YAxis yAxisId="bars" tick={axis} axisLine={false} tickLine={false} tickFormatter={(v) => compactKr(v)} width={64} />
          <YAxis yAxisId="line" orientation="right" hide />
          <Tooltip content={<Tip />} cursor={{ fill: "rgba(16,24,40,0.04)" }} />
          <Bar yAxisId="bars" dataKey="revenue" name="Inntekt" fill={C.primary} isAnimationActive={false} maxBarSize={22} radius={[3, 3, 0, 0]} />
          <Bar yAxisId="bars" dataKey="cost" name="Eksterne kostnader" fill={C.danger} fillOpacity={0.7} isAnimationActive={false} maxBarSize={22} radius={[3, 3, 0, 0]} />
          <Line yAxisId="line" type="monotone" dataKey="cum_revenue" name="Akkumulert inntekt" stroke={C.ink} strokeWidth={1.5} dot={false} isAnimationActive={false} />
          <Line yAxisId="line" type="monotone" dataKey="cum_cost" name="Akkumulert kostnad" stroke={C.danger} strokeWidth={1.25} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
    <Legend className="mt-2" items={[{ label: "Inntekt (fakturert)", color: C.primary }, { label: "Eksterne kostnader", color: C.danger }, { label: "Akkumulert inntekt", color: C.ink, line: true }, { label: "Akkumulert kostnad", color: C.danger, line: true }]} />
  </div>
);

/** Small sparkline for stat context. */
export const Spark = ({ data = [], dataKey = "v", height = 36, color = C.primary }) => (
  <div style={{ height }} className="w-full" aria-hidden>
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.25} fill={color} fillOpacity={0.12} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);
